import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  ChevronDown, Clock, Layers, LayoutGrid, Search, ShoppingBasket, Sparkles, Star,
} from 'lucide-react';
import { cn } from '@/lib/format';
import { t } from '@/lib/i18n';
import { LanguageSwitch } from '@/components/LanguageSwitch';
import { useArtworkStatus, useBasket, useLibraryStats } from '@/lib/queries';

/**
 * Library views as tabs across the top of the main area. They used to sit in
 * the rail under the folder tree, where people missed them; up here they are
 * the first thing on every page. File types are not tabs: the type buttons on
 * the listing already do that, inside whatever folder is open. As many tabs as
 * fit are shown in order; the rest fold into "More".
 */

interface Tab {
  label: string;
  icon: React.ReactNode;
  to: string;
  /** Search params that make up this view; `exact` = the bare path, no params. */
  search?: Record<string, unknown>;
  exact?: boolean;
  count?: number;
  /** Each tab has its own colour so the bar is easy to scan, not a row of grey. */
  tone: Tone;
}

interface Tone {
  /** Icon colour, always on. */
  icon: string;
  /** Text + tinted pill when the tab is open. */
  on: string;
}

const TONES = {
  brand: { icon: 'text-brand-500 dark:text-brand-400', on: 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300' },
  violet: { icon: 'text-violet-500 dark:text-violet-400', on: 'bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300' },
  sky: { icon: 'text-sky-500 dark:text-sky-400', on: 'bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300' },
  amber: { icon: 'text-amber-500 dark:text-amber-400', on: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300' },
  rose: { icon: 'text-rose-500 dark:text-rose-400', on: 'bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300' },
  yellow: { icon: 'text-yellow-500 dark:text-yellow-400', on: 'bg-yellow-50 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-300' },
  orange: { icon: 'text-orange-500 dark:text-orange-400', on: 'bg-orange-50 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300' },
} satisfies Record<string, Tone>;

/** Same rule the rail used: path matches and the search params are exactly these. */
function useIsActive() {
  const loc = useRouterState().location;
  const cur = new URLSearchParams(loc.searchStr);
  return (t: Tab) => {
    if (loc.pathname !== t.to) return false;
    if (!t.search) return true;
    // "All" stays lit while the listing is narrowed by type or re-sorted, since
    // those are the type buttons on the page, not another tab.
    if (t.exact) return !cur.has('untagged') && !cur.has('favourites') && cur.get('sort') !== 'created';
    const want = Object.entries(t.search);
    return want.every(([k, v]) => cur.get(k) === String(v)) && [...cur.keys()].length === want.length;
  };
}

export function TopTabs() {
  const { data: stats } = useLibraryStats();
  const { data: basket } = useBasket();
  const { data: artwork } = useArtworkStatus();
  const isActive = useIsActive();

  const main: Tab[] = [
    { label: t('topTabs.all'), icon: <LayoutGrid />, to: '/all', search: {}, exact: true, count: stats?.total, tone: TONES.brand },
    // Pimcore-only: hidden rather than leading to an empty page.
    ...(artwork?.present ? [{ label: t('topTabs.artwork'), icon: <Layers />, to: '/artwork', count: artwork.records, tone: TONES.violet }] : []),
    { label: t('topTabs.aiSearch'), icon: <Search />, to: '/search', tone: TONES.sky },
  ];
  const rest: Tab[] = [
    { label: t('topTabs.recent'), icon: <Clock />, to: '/all', search: { sort: 'created', order: 'desc' }, tone: TONES.amber },
    { label: t('topTabs.untagged'), icon: <Sparkles />, to: '/all', search: { untagged: true }, count: stats?.untagged, tone: TONES.rose },
    { label: t('topTabs.favourites'), icon: <Star />, to: '/all', search: { favourites: true }, count: stats?.favourites, tone: TONES.yellow },
    { label: t('topTabs.shareBasket'), icon: <ShoppingBasket />, to: '/basket', count: basket?.length, tone: TONES.orange },
  ];

  const all = [...main, ...rest];
  const shown = useFit(all, isActive);
  const visible = all.slice(0, shown.count);
  const more = all.slice(shown.count);

  return (
    <div className="flex h-12 shrink-0 items-center border-b line surface-card">
    <nav
      ref={shown.nav}
      aria-label={t('topTabs.library')}
      className="scroll-thin relative flex h-full min-w-0 flex-1 items-stretch gap-1 overflow-x-auto px-3"
    >
      {visible.map((t) => (
        <TabLink key={t.label} tab={t} active={isActive(t)} />
      ))}
      {more.length > 0 && <MoreMenu tabs={more} active={more.find(isActive)} isActive={isActive} />}
      {/* Off-screen copies of every tab, measured to decide how many fit. */}
      <div ref={shown.ruler} aria-hidden className="pointer-events-none invisible absolute left-0 top-0 flex h-0 overflow-hidden">
        {all.map((t) => (
          <span key={t.label} data-tab className={tabBase}><TabContent tab={t} /></span>
        ))}
        {[t('topTabs.more'), ...all.map((t) => t.label)].map((l) => (
          <span key={l} data-more={l} className={tabBase}>{l}<ChevronDown className="size-3.5" /></span>
        ))}
      </div>
    </nav>
      {/* Outside the nav: the nav scrolls horizontally and would clip the menu. */}
      <LanguageSwitch className="shrink-0 pr-3" />
    </div>
  );
}

const GAP = 4; // gap-1
const PAD = 24; // px-3 both sides

/**
 * How many tabs fit in the bar. Re-measures when the bar resizes or the tab
 * labels/counts change; until the first measure it shows everything.
 */
function useFit(tabs: Tab[], isActive: (t: Tab) => boolean) {
  const nav = useRef<HTMLElement>(null);
  const ruler = useRef<HTMLDivElement>(null);
  const [count, setCount] = useState(tabs.length);
  const key = tabs.map((t) => `${t.label}:${t.count ?? ''}`).join('|');
  const activeLabel = tabs.find(isActive)?.label;

  useLayoutEffect(() => {
    const el = nav.current;
    const r = ruler.current;
    if (!el || !r) return;
    const fit = () => {
      const w = (q: string) => [...r.querySelectorAll<HTMLElement>(q)].map((e) => e.getBoundingClientRect().width);
      const widths = w('[data-tab]');
      const moreW = (label: string) => (r.querySelector<HTMLElement>(`[data-more="${CSS.escape(label)}"]`)?.getBoundingClientRect().width ?? 0);
      const room = el.clientWidth - PAD;
      const used = (n: number) => widths.slice(0, n).reduce((a, b) => a + b + GAP, 0);
      if (used(widths.length) <= room) return setCount(widths.length);
      let n = widths.length - 1;
      for (; n > 0; n--) {
        // The More button names the open view when it is folded away, so size it with that label.
        const hiddenActive = tabs.slice(n).find(isActive);
        if (used(n) + moreW(hiddenActive?.label ?? t('topTabs.more')) <= room) break;
      }
      setCount(n);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    document.fonts?.ready.then(fit);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, activeLabel]);

  return { nav, ruler, count };
}

const tabBase =
  'my-2 flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 text-[13px] font-semibold transition-colors';
const tabIdle = 'text-body hover:surface-hover hover:text-strong';

function TabLink({ tab, active }: { tab: Tab; active: boolean }) {
  return (
    <Link
      to={tab.to}
      search={tab.search}
      className={cn(tabBase, active ? tab.tone.on : tabIdle)}
    >
      <TabContent tab={tab} active={active} />
    </Link>
  );
}

function TabContent({ tab, active }: { tab: Tab; active?: boolean }) {
  return (
    <>
      <span className={cn('flex [&>svg]:size-4 [&>svg]:shrink-0', tab.tone.icon)}>{tab.icon}</span>
      {tab.label}
      {tab.count != null && tab.count > 0 && (
        <span
          className={cn(
            'rounded-full px-1.5 text-[10px] font-medium tabular-nums',
            active ? 'bg-white/80 dark:bg-black/25' : 'bg-black/5 text-muted dark:bg-white/10',
          )}
        >
          {tab.count.toLocaleString()}
        </span>
      )}
    </>
  );
}

function MoreMenu({ tabs, active, isActive }: { tabs: Tab[]; active?: Tab; isActive: (t: Tab) => boolean }) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState({ top: 0, left: 0 });
  const box = useRef<HTMLDivElement>(null);
  const loc = useRouterState().location;

  useEffect(() => setOpen(false), [loc.href]);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={box} className="relative flex shrink-0 items-stretch">
      <button
        onClick={() => {
          // The bar scrolls sideways on narrow screens, so the menu is fixed and
          // placed under the button rather than clipped inside the bar.
          const r = box.current?.getBoundingClientRect();
          if (r) setAt({ top: r.bottom + 4, left: Math.min(r.left, window.innerWidth - 200) });
          setOpen((o) => !o);
        }}
        aria-expanded={open}
        className={cn(tabBase, active ? active.tone.on : tabIdle)}
      >
        {/* Says which hidden view is open, so the active tab is never invisible. */}
        {active ? active.label : t('topTabs.more')}
        <ChevronDown className="size-3.5" />
      </button>
      {open && (
        <div style={at} className="fixed z-50 min-w-44 rounded-lg border line surface-card p-1 shadow-lg">
          {tabs.map((t) => (
            <Link
              key={t.label}
              to={t.to}
              search={t.search}
              className={cn(
                'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px]',
                isActive(t) ? t.tone.on : 'text-body hover:surface-hover',
              )}
            >
              <span className={cn('flex [&>svg]:size-4', t.tone.icon)}>{t.icon}</span>
              <span className="flex-1">{t.label}</span>
              {t.count != null && t.count > 0 && <span className="text-[10px] tabular-nums text-muted">{t.count.toLocaleString()}</span>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
